import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../../config/theme';
import { motion, AnimatePresence } from 'framer-motion';
import AdBanner from './AdBanner';

/**
 * GiveawaySection — Combined ad banner + phone number giveaway engagement block.
 * Matches the reference image design:
 *  - Ad banner area (red bordered, clickable)
 *  - Gift box image
 *  - "Enter your phone number to win exciting gifts" heading
 *  - Phone number input + Submit button
 */
const GiveawaySection = ({
  adImageUrl,
  adLinkUrl,
  adTitle = 'Exclusive Deals from Our Partners',
  adDescription = 'Click to explore amazing offers curated just for you!',
  giftImageUrl,
  giveawayTitle = 'Enter your phone number here to participate and win exciting gifts',
  giveawayDescription = 'Join the AlarcTrade giveaway! Lucky winners will be contacted directly.',
  onPhoneSubmit,
  onAdClick,
}) => {
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const inputRef = useRef(null);

  // Generate confetti particles once
  const [confettiPieces] = useState(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A78BFA', '#FB923C', '#34D399'][Math.floor(Math.random() * 6)],
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
    }))
  );

  const validatePhone = (value) => {
    // Allow digits, spaces, dashes, parentheses, and leading +
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length < 10) return 'Please enter a valid phone number (at least 10 digits)';
    if (cleaned.length > 15) return 'Phone number is too long';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validatePhone(phone);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Call parent callback if provided (e.g., API submission)
      if (onPhoneSubmit) {
        await onPhoneSubmit(phone);
      }

      // Simulate brief network delay for demo feel
      await new Promise(resolve => setTimeout(resolve, 600));

      setSubmitted(true);
      setShowConfetti(true);

      // Hide confetti after animation
      setTimeout(() => setShowConfetti(false), 3000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits, spaces, dashes, parentheses, and leading +
    if (/^[+]?[\d\s\-()]*$/.test(value)) {
      setPhone(value);
      if (error) setError('');
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{
        marginTop: '3rem',
        marginBottom: '1rem',
        position: 'relative',
      }}
    >
      {/* ── Ad Banner Area ─────────────────────────────── */}
      {adImageUrl && (
        <div style={{ marginBottom: '2.5rem' }}>
          <AdBanner
            imageUrl={adImageUrl}
            linkUrl={adLinkUrl}
            title={adTitle}
            description={adDescription}
            size="full"
            style={{ minHeight: '200px', height: '200px' }}
            onAdClick={onAdClick}
          />
        </div>
      )}

      {/* ── Giveaway Engagement Block ──────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #FFF5F5 0%, #FFF0E5 40%, #FFFBE5 100%)',
        borderRadius: theme.borderRadius['2xl'] || '1rem',
        padding: '2.5rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
        border: `1px solid rgba(229, 57, 53, 0.1)`,
      }}>
        {/* Background decorative circles */}
        <div style={{
          position: 'absolute',
          top: '-40px',
          right: '-40px',
          width: '140px',
          height: '140px',
          borderRadius: '50%',
          background: 'rgba(229, 57, 53, 0.04)',
          zIndex: 0,
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(255, 183, 77, 0.08)',
          zIndex: 0,
        }} />

        {/* Confetti animation overlay */}
        <AnimatePresence>
          {showConfetti && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              pointerEvents: 'none',
              zIndex: 10,
              overflow: 'hidden',
            }}>
              {confettiPieces.map(piece => (
                <motion.div
                  key={piece.id}
                  initial={{
                    x: `${piece.x}%`,
                    y: '-10%',
                    rotate: 0,
                    opacity: 1,
                  }}
                  animate={{
                    y: '110%',
                    rotate: piece.rotation + 360,
                    opacity: [1, 1, 0],
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 2 + Math.random(),
                    delay: piece.delay,
                    ease: 'easeIn',
                  }}
                  style={{
                    position: 'absolute',
                    width: `${piece.size}px`,
                    height: `${piece.size}px`,
                    backgroundColor: piece.color,
                    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                  }}
                />
              ))}
            </div>
          )}
        </AnimatePresence>

        {/* Gift box image */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'relative', zIndex: 1, marginBottom: '1.5rem' }}
        >
          <img
            src={giftImageUrl || '/images/giveaway_gift.png'}
            alt="Exciting gifts await!"
            style={{
              width: '160px',
              height: '160px',
              objectFit: 'contain',
              margin: '0 auto',
              display: 'block',
              filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.12))',
            }}
          />
        </motion.div>

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{
            fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
            fontWeight: theme.typography.fontWeight.bold,
            background: 'linear-gradient(135deg, #E53935 0%, #FF8F00 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem',
            lineHeight: 1.3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {giveawayTitle}
        </motion.h2>

        <p style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.text.secondary,
          marginBottom: '1.5rem',
          position: 'relative',
          zIndex: 1,
        }}>
          {giveawayDescription}
        </p>

        {/* Phone form / Success state */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form
                key="form"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleSubmit}
                style={{
                  maxWidth: '440px',
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                }}
              >
                <div style={{
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.xl,
                  border: `1.5px solid ${error ? '#E53935' : theme.colors.gray[200]}`,
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  transition: 'border-color 0.2s ease',
                }}>
                  {/* Phone icon */}
                  <div style={{
                    padding: '0 12px',
                    color: theme.colors.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <input
                    ref={inputRef}
                    type="tel"
                    placeholder="Phone number to be entered here by users who want to participate in giveaway."
                    value={phone}
                    onChange={handlePhoneChange}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      padding: '14px 8px',
                      fontSize: theme.typography.fontSize.base,
                      backgroundColor: 'transparent',
                      color: theme.colors.text.primary,
                      minWidth: 0,
                    }}
                  />
                </div>

                {/* Error message */}
                <AnimatePresence>
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      style={{
                        color: '#E53935',
                        fontSize: theme.typography.fontSize.sm,
                        margin: 0,
                        textAlign: 'left',
                        paddingLeft: '4px',
                      }}
                    >
                      {error}
                    </motion.p>
                  )}
                </AnimatePresence>

                {/* Submit button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting || !phone.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: 'linear-gradient(135deg, #5EAAA8 0%, #4A9896 100%)',
                    color: theme.colors.white,
                    border: 'none',
                    borderRadius: theme.borderRadius.full,
                    padding: '14px 40px',
                    fontSize: theme.typography.fontSize.base,
                    fontWeight: theme.typography.fontWeight.bold,
                    cursor: isSubmitting || !phone.trim() ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting || !phone.trim() ? 0.6 : 1,
                    transition: 'opacity 0.2s ease',
                    boxShadow: '0 4px 14px rgba(94, 170, 168, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    margin: '0 auto',
                    minWidth: '180px',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        style={{
                          width: '18px',
                          height: '18px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: theme.colors.white,
                          borderRadius: '50%',
                        }}
                      />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </motion.button>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                style={{
                  maxWidth: '440px',
                  margin: '0 auto',
                  padding: '1.5rem',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: theme.borderRadius.xl,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎉</div>
                <h3 style={{
                  fontSize: theme.typography.fontSize.xl,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: '#2E7D32',
                  marginBottom: '0.5rem',
                }}>
                  You're In!
                </h3>
                <p style={{
                  color: theme.colors.text.secondary,
                  fontSize: theme.typography.fontSize.sm,
                  marginBottom: '1rem',
                }}>
                  Thank you for participating! We'll contact you if you're a lucky winner.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setPhone('');
                    setShowConfetti(false);
                  }}
                  style={{
                    background: 'none',
                    border: `1px solid ${theme.colors.gray[300]}`,
                    borderRadius: theme.borderRadius.full,
                    padding: '8px 20px',
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.text.secondary,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.gray[50];
                    e.currentTarget.style.borderColor = theme.colors.gray[400];
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = theme.colors.gray[300];
                  }}
                >
                  Enter another number
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.section>
  );
};

export default GiveawaySection;
